import { flexRender, type Table } from '@tanstack/react-table';
import { useWindowSize } from '@uidotdev/usehooks';
import { type ReactNode, useLayoutEffect, useRef } from 'react';
import { IoChevronBack, IoChevronForward } from 'react-icons/io5';
import { styled } from 'styled-components';
import { mediaQuery } from '../../styles/styles.js';
import { FuuzeButton } from '../fuuze-button.js';
import { FlexColumn, FlexRow } from '../fuuze-flex.js';
import { FuuzeLoading } from '../fuuze-loading.js';
import { calculateTableSizing } from './fuuze-table-resizer.js';

type FuuzeTableProps<T> = {
  table: Table<T>;
  Header?: ReactNode;
  hidePagination?: boolean;
  tbodyTestId?: string;
};

export function FuuzeTable<T>({ table, Header, hidePagination, tbodyTestId }: FuuzeTableProps<T>) {
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const { isLoading, EmptyComponent, overrideGetCanNextPage, pagination } = table.getState();
  const windowDimensions = useWindowSize();
  const tbodyRef = useRef<HTMLTableSectionElement>(null);

  const headers = table.getFlatHeaders();

  useLayoutEffect(() => {
    if (tableContainerRef.current) {
      const initialColumnSizing = calculateTableSizing(headers, tableContainerRef.current?.clientWidth);
      table.setColumnSizing(initialColumnSizing);
    }
  }, [headers, windowDimensions.width]);

  return (
    <Wrapper ref={tableContainerRef}>
      {Header}
      <TableStyled>
        <thead>
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => (
                <th
                  key={header.id}
                  colSpan={header.colSpan}
                  style={{
                    width: header.getSize(),
                    textAlign: header.column.columnDef.meta?.style?.headerAlign,
                  }}
                >
                  {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                </th>
              ))}
            </tr>
          ))}
        </thead>

        <tbody ref={tbodyRef} data-testid={tbodyTestId}>
          {isLoading ? (
            <LoadingComponent height={tbodyRef.current?.clientHeight} colspan={table.getAllLeafColumns().length} />
          ) : table.getRowModel().rows.length === 0 ? (
            <tr>{<td colSpan={table.getAllLeafColumns().length}>{EmptyComponent}</td>}</tr>
          ) : (
            table.getRowModel().rows.map(row => (
              <tr key={row.id} onClick={() => table.toggleRowClick(row.original)}>
                {row.getVisibleCells().map(cell => (
                  <td
                    key={cell.id}
                    className={cell.column.id}
                    style={{
                      width: cell.column.getSize(),
                      textAlign: cell.column.columnDef.meta?.style?.textAlign,
                    }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>

        {!hidePagination && (
          <tfoot>
            <tr>
              <td colSpan={table.getAllLeafColumns().length}>
                <FlexRow style={{ justifyContent: 'end', alignItems: 'center', gap: 8, margin: 8 }}>
                  {isLoading ? (
                    <FuuzeLoading style={{ margin: '4px 16px' }} $color='light' />
                  ) : (
                    <>
                      <FuuzeButton icon disabled={!table.getCanPreviousPage()} onClick={() => table.previousPage()}>
                        <IoChevronBack size={20} />
                      </FuuzeButton>
                      <span>{pagination.pageIndex + 1}</span>
                      <FuuzeButton
                        icon
                        disabled={overrideGetCanNextPage ? !overrideGetCanNextPage() : !table.getCanNextPage()}
                        onClick={() => table.nextPage()}
                      >
                        <IoChevronForward size={20} />
                      </FuuzeButton>
                    </>
                  )}
                </FlexRow>
              </td>
            </tr>
          </tfoot>
        )}
      </TableStyled>
    </Wrapper>
  );
}

function LoadingComponent({ colspan, height }: { colspan: number; height?: number }) {
  return (
    <tr style={{ height: height || 300 }}>
      <td colSpan={colspan}>
        <div style={{ display: 'grid', placeItems: 'center' }}>
          <FuuzeLoading $color='light' />
        </div>
      </td>
    </tr>
  );
}

const Wrapper = styled(FlexColumn)`
  border: 1px solid ${({ theme }) => theme.colors.dark.dark300};
  border-radius: 8px;
  overflow-x: auto;
  background-color: ${({ theme }) => theme.colors.background};
`;

const TableStyled = styled.table<{ $isEmpty?: boolean }>`
  border-collapse: collapse;
  border-spacing: 0;
  overflow: hidden;
  width: 100%;

  thead {
    tr {
      background-color: ${({ theme }) => theme.colors.background} !important;
    }

    th {
      padding: 8px;

      @media ${mediaQuery.tablet} {
        padding: 16px;
      }
    }
  }

  tbody {
    tr {
      border-radius: 4px;
      padding: 16px 0;

      &:nth-child(odd) {
        background-color: ${props =>
          props.$isEmpty ? 'transparent' : 'color-mix(in srgb, currentColor 2%, white 1%)'};
      }

      td {
        text-align: center;
        padding: 4px;

        @media ${mediaQuery.tablet} {
          padding: 8px;
        }

        &.title {
          text-align: left;
        }
      }
    }
  }

  tfoot {
    tr {
      background-color: ${({ theme }) => theme.colors.background};
    }
  }
`;
